import {
  useLegalClientBeneficiaryArchive,
  useLegalClientBeneficiaryDearchive,
  useLegalClientBranchArchive,
  useLegalClientBranchDearchive,
  useLegalClientHeadArchive,
  useLegalClientHeadDearchive,
  useLegalClientLegalHeadArchive,
  useLegalClientLegalHeadDearchive,
} from 'api/requests';
import { LegalClientEntity, PermissionType } from 'api/types/entity';
import { useServerErrorNotify } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';
import { usePermissions } from 'libs/permissions';

export const useArchiveParticipants = (
  legalClient: LegalClientEntity | null | undefined,
  refetchLegalClient: () => void
) => {
  const { t } = useTranslation();
  const notify = useNotify();

  const { checkPermissions } = usePermissions();

  const { archive: archiveHead, error: archiveHeadError } =
    useLegalClientHeadArchive();
  const { archive: archiveLegalHead, error: archiveLegalHeadError } =
    useLegalClientLegalHeadArchive();
  const { dearchive: dearchiveHead, error: dearchiveHeadError } =
    useLegalClientHeadDearchive();
  const { dearchive: dearchiveLegalHead, error: dearchiveLegalHeadError } =
    useLegalClientLegalHeadDearchive();

  const { archive: archiveBeneficiary, error: archiveBeneficiaryError } =
    useLegalClientBeneficiaryArchive();
  const { dearchive: dearchiveBeneficiary, error: dearchiveBeneficiaryError } =
    useLegalClientBeneficiaryDearchive();

  const { archive: archiveBranch, error: archiveBranchError } =
    useLegalClientBranchArchive();

  const { dearchive: dearchiveBranch, error: dearchiveBranchError } =
    useLegalClientBranchDearchive();

  const handleSuccess = async (message: string) => {
    await refetchLegalClient();
    notify.info(message);
  };

  const onArchiveHead = checkPermissions([
    PermissionType.LegalClientChildEntity_ArchiveOne,
  ])
    ? async (id: string, reason: string) => {
        await archiveHead(id, {
          reason,
          versionNumber: legalClient?.versionNumber!,
        });
        await handleSuccess(t('common.archivedMessage'));
      }
    : undefined;

  const onArchiveLegalHead = checkPermissions([
    PermissionType.LegalClientChildEntity_ArchiveOne,
  ])
    ? async (id: string, reason: string) => {
        await archiveLegalHead(id, {
          reason,
          versionNumber: legalClient?.versionNumber!,
        });
        await handleSuccess(t('common.archivedMessage'));
      }
    : undefined;

  const onDearchiveHead = checkPermissions([
    PermissionType.LegalClientChildEntity_ArchiveOne,
  ])
    ? async (id: string, reason: string) => {
        const updatedHead = await dearchiveHead(id, {
          reason,
          versionNumber: legalClient?.versionNumber!,
        });
        await handleSuccess(t('common.dearchivedMessage'));
        if (
          updatedHead &&
          !updatedHead.legalClientBodies?.length &&
          updatedHead.hasLegalClientBody
        ) {
          notify.show(
            'error',
            t('client.legal.legalClientHead.bodyErrorMessage')
          );
        }
      }
    : undefined;

  const onDearchiveLegalHead = checkPermissions([
    PermissionType.LegalClientChildEntity_ArchiveOne,
  ])
    ? async (id: string, reason: string) => {
        const updatedLegalHead = await dearchiveLegalHead(id, {
          reason,
          versionNumber: legalClient?.versionNumber!,
        });
        await handleSuccess(t('common.dearchivedMessage'));
        if (
          updatedLegalHead &&
          !updatedLegalHead.legalClientBodies?.length &&
          updatedLegalHead.hasLegalClientBody
        ) {
          notify.show(
            'error',
            t('client.legal.legalClientHead.bodyErrorMessage')
          );
        }
      }
    : undefined;

  const onArchiveBeneficiary = checkPermissions([
    PermissionType.LegalClientChildEntity_ArchiveOne,
  ])
    ? async (id: string, reason: string) => {
        await archiveBeneficiary(id, {
          reason,
          versionNumber: legalClient?.versionNumber!,
        });
        await handleSuccess(t('common.archivedMessage'));
      }
    : undefined;

  const onDearchiveBeneficiary = checkPermissions([
    PermissionType.LegalClientChildEntity_ArchiveOne,
  ])
    ? async (id: string, reason: string) => {
        await dearchiveBeneficiary(id, {
          reason,
          versionNumber: legalClient?.versionNumber!,
        });
        await handleSuccess(t('common.dearchivedMessage'));
      }
    : undefined;

  const onArchiveBranch = checkPermissions([
    PermissionType.LegalClientChildEntity_ArchiveOne,
  ])
    ? async (id: string, reason: string) => {
        await archiveBranch(id, {
          reason,
          versionNumber: legalClient?.versionNumber!,
        });
        await handleSuccess(t('common.archivedMessage'));
      }
    : undefined;

  const onDearchiveBranch = checkPermissions([
    PermissionType.LegalClientChildEntity_ArchiveOne,
  ])
    ? async (id: string, reason: string) => {
        await dearchiveBranch(id, {
          reason,
          versionNumber: legalClient?.versionNumber!,
        });
        await handleSuccess(t('common.dearchivedMessage'));
      }
    : undefined;

  useServerErrorNotify(
    archiveHeadError ??
      archiveLegalHeadError ??
      dearchiveHeadError ??
      dearchiveLegalHeadError ??
      archiveBeneficiaryError ??
      dearchiveBeneficiaryError ??
      archiveBranchError ??
      dearchiveBranchError
  );

  return {
    onArchiveHead,
    onArchiveLegalHead,
    onDearchiveLegalHead,
    onDearchiveHead,
    onArchiveBeneficiary,
    onDearchiveBeneficiary,
    onArchiveBranch,
    onDearchiveBranch,
  };
};
